import moment from 'moment';
import { computeDroppedDueDate } from '../../drop-datetime';
import type { CalendarGridView, CalendarLeanEvent } from '../types';
import { isProjectedDripStep } from './occurrence-key';
import { campaignOutcomeOf } from '@common/services/planner-filters';

export type DragRefusal =
    | 'sequence-step'
    | 'campaign-too-close'
    | 'campaign-started'
    | 'already-sent';

/**
 * Is this chip the RECORD of a send that already happened, rather than a plan? A message's
 * sent record (the soft-deleted row the backend keeps), a campaign that went out, a
 * completed sequence step, or the chip drawn from a finished campaign's failures. Moving
 * one would write to history — and for a step it would re-time the rest of the sequence
 * from a send that is over.
 *
 * Deliberately NOT "any chip whose status reads sent": a LIVE series document can carry a
 * handed-off status while its next occurrences are still plans, and those keep every
 * affordance they had.
 */
export function isResolvedSendChip(event: Pick<CalendarLeanEvent, 'isCampaign' | 'campaignStatus' | 'effectiveStatus' | 'isSentRecord'>): boolean {
    if (event.isCampaign) {
        return campaignOutcomeOf(event.campaignStatus) === 'sent'
            || (!event.campaignStatus && !!event.effectiveStatus);
    }
    return event.isSentRecord === true;
}

export interface DragEligibility {
    draggable: boolean;
    refusal?: DragRefusal;
}

/** Mirrors onCampaignEventDropThunk's `too_close` guard: a campaign whose send
 *  is between now and five minutes from now is already being handed off. */
const CAMPAIGN_LOCK_MINUTES = 5;

/**
 * Minutes until a send, counted THE WAY THE THUNK COUNTS THEM.
 *
 * `onCampaignEventDropThunk` asks moment for `diff(now, 'minutes')`, and moment's
 * diff is `absFloor` — it truncates toward zero and never reports a fraction. This
 * predicate exists solely so the chip agrees with that guard, so it has to do the
 * same arithmetic; dividing by 60_000 and comparing the real number does not.
 *
 * Two bands where the difference is the whole bug this function was written to
 * prevent — a chip that looks draggable, drags, and bounces back with an error:
 *
 *   - due in [5min, 6min): moment says 5, so the thunk REFUSES. A raw 5.5 <= 5 is
 *     false, so the chip said "yes".
 *   - due 0–59 seconds AGO: moment says 0 (it truncates -0.5 to zero), which is
 *     `>= 0 && <= 5`, so the thunk REFUSES. A raw -0.5 >= 0 is false, so the chip
 *     said "yes" — and this band is reached by simply waiting, without moving the
 *     pointer, on any campaign the user was already looking at.
 */
const minutesUntilDueAsMomentCountsThem = (dueMs: number, now: number): number =>
    Math.trunc((dueMs - now) / 60_000);

/**
 * Can this event be dragged at all? Decided BEFORE the drag starts, so a
 * refusal shows as "you can't move this" on the chip rather than as a warning
 * dialog after the user has already dropped it.
 */
export function canDragEvent(event: CalendarLeanEvent, now: number): DragEligibility {
    // First, before the drip rule below: a completed step IS a drip step, and the drip rule
    // would otherwise hand it a re-timing drag.
    if (isResolvedSendChip(event)) {
        return { draggable: false, refusal: event.isCampaign ? 'campaign-started' : 'already-sent' };
    }
    // A drip step IS draggable — dragging it re-times the sequence rather than
    // moving one date (see `resolveDripStepDrop`). The one case that cannot be
    // expressed — re-dating the START of a repeating sequence — is refused on
    // DROP rather than here, because whether the sequence repeats lives on the
    // drip document and this lean event carries only the campaign's own flag.
    if (event.dripId) {
        return { draggable: true };
    }
    // A projected step with no drip linkage is not addressable at all: the id
    // `dripId:step:N` is not a campaign document, so any write would miss.
    if (isProjectedDripStep(event._id)) {
        return { draggable: false, refusal: 'sequence-step' };
    }
    if (event.isCampaign) {
        // The thunk also rejects any campaign that is no longer pending. Without
        // the same rule here the chip looks draggable, drags, and then bounces
        // back with an error — the UI promising something the rules forbid.
        if (event.campaignStatus && event.campaignStatus !== 'pending') {
            return { draggable: false, refusal: 'campaign-started' };
        }
        const minutesUntilDue = minutesUntilDueAsMomentCountsThem(
            new Date(event.dueDate).getTime(), now,
        );
        if (minutesUntilDue >= 0 && minutesUntilDue <= CAMPAIGN_LOCK_MINUTES) {
            return { draggable: false, refusal: 'campaign-too-close' };
        }
    }
    return { draggable: true };
}

/** Floor a timestamp to the slot grid (15 minutes in week/day). */
export function snapToSlot(timestampMs: number, slotMinutes: number): number {
    const slotMs = slotMinutes * 60_000;
    const dayStart = moment(timestampMs).startOf('day').valueOf();
    return dayStart + Math.floor((timestampMs - dayStart) / slotMs) * slotMs;
}

export type DropResolution =
    | { kind: 'noop' }
    | { kind: 'apply'; dueDate: string }
    | { kind: 'past-warning'; dueDate: string }
    | { kind: 'recurring-scope-prompt'; dueDate: string };

/**
 * Decide what a drop MEANS. Nothing here mutates — the caller dispatches.
 *
 * Month keeps the source time of day (FE#264, via computeDroppedDueDate);
 * week/day take the dropped instant. A recurring occurrence never applies
 * directly: it opens the scope dialog, and the chip stays where it was until
 * the store changes.
 */
/** How long after a drop a click still counts as that drop's own click. */
export const CLICK_SUPPRESSION_MS = 400;

/**
 * Should the click now being handled be swallowed?
 *
 * A chip captures the pointer while it is dragged, so the browser delivers the
 * click that follows the drop TO THAT CHIP — which reads it as a plain select
 * and opens the chip's Edit/Delete menu. After a drop that is wrong twice over:
 * the user asked to move the event, and on a recurring one the menu lands on top
 * of the scope dialog the drop just raised.
 *
 * Bounded in time as well as consumed once: a drag whose click never arrives —
 * released outside the chip, or over a dialog — must not swallow the next real
 * click minutes later.
 */
export function isDragTailClick(draggedAt: number | null, now: number = Date.now()): boolean {
    return draggedAt != null && now - draggedAt < CLICK_SUPPRESSION_MS;
}

/**
 * When a gesture ends, the instant to remember so its own release is swallowed —
 * or null when there is nothing to swallow.
 *
 * The rule is about the GESTURE, not about how it finished: anything that crossed
 * the drag threshold ends in a release the browser reports as a click on the chip,
 * and that click was never the user's intent.
 *
 * Suppression used to be armed only where a drag ended in a DROP. A drag cancelled
 * with Escape armed nothing, so its release fell through to the chip's plain click
 * handler and opened it — the user aborted a move precisely because they had
 * decided against it, and was punished by being navigated off the calendar and out
 * of the month they were working in.
 *
 * A press that never moved is deliberately NOT suppressed: it is an ordinary click
 * and opening the chip is exactly what it should do.
 */
export function clickSuppressionMark(gestureWasActive: boolean, endedAt: number): number | null {
    return gestureWasActive ? endedAt : null;
}

export function resolveDrop(
    event: CalendarLeanEvent,
    targetMs: number,
    view: CalendarGridView,
    now: number = Date.now(),
): DropResolution {
    const dueDate = view === 'month'
        // A month cell resolves to the day's midnight and nothing more, so it has
        // no time of day to offer — say so, or an intra-day drop silently re-times
        // the message to 00:00 (and stops looking like the no-op it is).
        ? computeDroppedDueDate(event.dueDate, new Date(targetMs), false)
        : moment(targetMs).startOf('minute').toISOString();

    if (dueDate === moment(event.dueDate).startOf('minute').toISOString()) {
        return { kind: 'noop' };
    }
    if (event.isRecurring && event.rruleset) {
        return { kind: 'recurring-scope-prompt', dueDate };
    }
    // A past cell looks exactly like a future one, so dropping into yesterday is
    // an easy accident — and the message simply never sends. Ask first; the user
    // may genuinely be tidying history, but they should know which they did.
    if (new Date(dueDate).getTime() < now) {
        return { kind: 'past-warning', dueDate };
    }
    return { kind: 'apply', dueDate };
}

/**
 * Which column of a `columns`-wide grid the pointer is over.
 *
 * The grid is laid out with `grid-template-columns`, which FLOWS with the
 * document direction: in Hebrew (`dir="rtl"`, set on <html> and <body> for
 * every RTL language) the first day renders on the RIGHT. Measuring the pointer
 * physically from the left edge therefore mirrors the answer, and a Hebrew user
 * dragging a message onto Thursday silently moved it to Monday — with no undo.
 *
 * Pure, so the mirroring is pinned by tests rather than by eye.
 */
export function columnIndexAt(
    clientX: number,
    rect: { left: number; width: number },
    columns: number,
    isRtl: boolean,
): number | null {
    if (!rect.width || columns <= 0) return null;
    const fromLeft = (clientX - rect.left) / rect.width;
    // Mirror the INDEX, not the fraction. Mirroring the fraction (`1 - fromLeft`)
    // moves the exclusive edge to the wrong side: the pixel exactly on `rect.left`
    // gives fraction 1 and floors to `columns`, which reads as "off the grid" —
    // but in RTL that pixel is the LAST column, and it is the one a Hebrew user
    // hits when they drag to the far edge of the week.
    const physical = Math.floor(fromLeft * columns);
    const index = isRtl ? columns - 1 - physical : physical;
    return index < 0 || index >= columns ? null : index;
}
